import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { useInjection } from '@core/providers/inversify-react.provider'
import { I18nPresenter } from './i18n.presenter'

/**
 * Reads I18nPresenter.viewModel and nothing else: which options exist, what
 * they are called and which one is selected are all decided there, from
 * TranslationsConfig. Adding a locale is a config edit — this file does not
 * change.
 */
export const LanguageSwitcherComponent = observer(() => {
  const presenter: I18nPresenter = useInjection(I18nPresenter)
  const [open, setOpen] = useState(false)
  const vm = presenter.viewModel

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-x-1 rounded-md px-2 py-1 text-shell-ink-muted hover:bg-shell-surface-muted hover:text-shell-ink"
        aria-label={vm.groupLabel}
      >
        <span aria-hidden="true" className="text-base leading-none">
          {vm.currentFlag}
        </span>
        <span className="text-xs font-semibold uppercase">{presenter.locale}</span>
        <ChevronDownIcon aria-hidden="true" className="size-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            role="group"
            aria-label={vm.groupLabel}
            className="absolute right-0 top-full z-20 mt-2.5 w-max min-w-36 origin-top-right whitespace-nowrap rounded-md bg-shell-surface py-2 shadow-lg outline-1 outline-shell-border"
          >
            {vm.options.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={() => {
                  presenter.setLocale(option.code)
                  setOpen(false)
                }}
                aria-pressed={option.isSelected}
                className={
                  option.isSelected
                    ? 'mx-2 flex w-[calc(100%-1rem)] items-center gap-x-2 rounded-md px-3 py-1 text-left text-sm/6 font-semibold text-shell-accent bg-shell-surface-muted'
                    : 'mx-2 flex w-[calc(100%-1rem)] items-center gap-x-2 rounded-md px-3 py-1 text-left text-sm/6 text-shell-ink hover:bg-shell-surface-muted'
                }
              >
                <span aria-hidden="true" className="text-base leading-none">
                  {option.flag}
                </span>
                <span className="text-xs font-semibold uppercase">{option.code}</span>
                <span className="text-shell-ink-muted">{option.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
})
